import type { Settings } from '@/types/settings';

interface AutoRenew {
  label: 'off' | 'on';
  value: number;
}

interface AccountSubscription {
  autoRenew?: AutoRenew;
  billing_cycle?: string;
  isFree?: boolean;
  next_bill_date?: string;
}

/**
 * Account summary in the shape the Symfony `api-customer/account` endpoint
 * returned, limited to the fields the frontend reads. Built from settings
 * (the bootstrap response) rather than fetched separately.
 */
export interface AccountDetails {
  creditsRenewAt?: string;
  form: {
    subscription: AccountSubscription;
  };
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Names of the subscription_payment_period rows, keyed by id. */
const BILLING_CYCLES: Record<number, string> = {
  1: 'Monthly',
  2: 'Annually',
  3: 'Daily',
  4: 'Weekly',
};

/**
 * Formats the date part of an ISO date or date-time as Symfony did
 * (`05 Oct 2026`, or `5 Oct 2026` without padding). Splits the string rather
 * than going through `Date` so the day can't shift with the viewer's timezone.
 */
export const formatLegacyDate = (isoDate: string, padDay: boolean): string => {
  const [year, month, day] = isoDate.slice(0, 10).split('-').map(Number);

  return `${padDay ? String(day).padStart(2, '0') : day} ${MONTHS[month - 1]} ${year}`;
};

const mapSubscription = (
  subscription: Settings['subscriptionData']
): AccountSubscription => {
  if (!subscription?.hasSubscription) {
    return {};
  }

  if (subscription.isFree) {
    return { isFree: true, billing_cycle: 'Free Plan' };
  }

  const mapped: AccountSubscription = {
    isFree: false,
    billing_cycle: BILLING_CYCLES[subscription.period],
    next_bill_date: subscription.nextBillingDate
      ? formatLegacyDate(subscription.nextBillingDate, true)
      : 'N/A',
  };

  if (typeof subscription.autoRenew === 'boolean') {
    mapped.autoRenew = subscription.autoRenew
      ? { value: 1, label: 'on' }
      : { value: 0, label: 'off' };
  }

  return mapped;
};

export const mapAccountDetails = (settings: Settings): AccountDetails => ({
  creditsRenewAt: settings.user.renewed_download_date
    ? formatLegacyDate(settings.user.renewed_download_date, false)
    : undefined,
  form: {
    subscription: mapSubscription(settings.subscriptionData),
  },
});
